import { useEffect, useRef, useState } from 'react';
import { Extension, generateHTML } from '@tiptap/core';
import RichTextEditor, { BaseKit } from 'reactjs-tiptap-editor';
import { Blockquote } from 'reactjs-tiptap-editor/blockquote';
import { Bold } from 'reactjs-tiptap-editor/bold';
import { BulletList } from 'reactjs-tiptap-editor/bulletlist';
import { Clear } from 'reactjs-tiptap-editor/clear';
import { Code } from 'reactjs-tiptap-editor/code';
import { CodeBlock } from 'reactjs-tiptap-editor/codeblock';
import { Color } from 'reactjs-tiptap-editor/color';
import { Heading } from 'reactjs-tiptap-editor/heading';
import { History } from 'reactjs-tiptap-editor/history';
import { HorizontalRule } from 'reactjs-tiptap-editor/horizontalrule';
import { Image } from 'reactjs-tiptap-editor/image';
import { Italic } from 'reactjs-tiptap-editor/italic';
import { Link } from 'reactjs-tiptap-editor/link';
import { MoreMark } from 'reactjs-tiptap-editor/moremark';
import { OrderedList } from 'reactjs-tiptap-editor/orderedlist';
import { Strike } from 'reactjs-tiptap-editor/strike';
import { TextAlign } from 'reactjs-tiptap-editor/textalign';
import { TextUnderline } from 'reactjs-tiptap-editor/textunderline';
import { type JSONContent } from '@tiptap/react';
import { GhostLoader } from '../GhostLoader';
import { ArticleUtils } from '../../utils/ArticleUtils/ArticleUtils';
import { uploadBlob } from '../../api/queries';

import classes from './ContentEditor.module.css';

import 'reactjs-tiptap-editor/style.css';
import 'prism-code-editor-lightweight/layout.css';
import 'prism-code-editor-lightweight/themes/github-dark.css';

import 'katex/dist/katex.min.css';
import 'easydrawer/styles.css';
import 'react-image-crop/dist/ReactCrop.css';
import '@excalidraw/excalidraw/index.css';

const uploadFile = async (file: File): Promise<string> => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024;

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only images are allowed.');
  }

  if (file.size > maxSize) {
    throw new Error('Filen er for stor, max størrelse er 5MB.');
  }

  const res = await uploadBlob({ file });

  try {
    if (!res.url) {
      throw new Error('Upload response missing URL');
    }

    return res.url as string;
  } catch (error) {
    console.error('File upload failed:', error);

    throw new Error('Failed to upload file. Please try again.');
  }
};

const extensions: Extension[] = [
  BaseKit.configure({
    placeholder: {
      showOnlyCurrent: true,
      placeholder: 'Skriv inn tekst her',
    },
    characterCount: false,
  }) as Extension,
  History as Extension,
  Clear as Extension,
  Heading.configure({ spacer: true }) as Extension,
  Bold as Extension,
  Italic as Extension,
  TextUnderline as Extension,
  Strike as Extension,
  MoreMark as Extension,
  Color.configure({ spacer: true }) as Extension,
  BulletList as Extension,
  OrderedList as Extension,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    spacer: true,
  }) as Extension,
  Link as Extension,
  Image.configure({
    upload: (file: File) => uploadFile(file),
  }) as Extension,
  Blockquote as Extension,
  HorizontalRule as Extension,
  Code.configure({
    toolbar: false,
  }) as Extension,
  CodeBlock as Extension,
];

type ContentEditorProps = {
  content: JSONContent | null | undefined;
  onChange: (doc: JSONContent) => void;
};

export const ContentEditor = ({ content, onChange }: ContentEditorProps) => {
  const skipFirstChange = useRef(true);
  const [initialHtml, setInitialHtml] = useState<string | null>(null);

  useEffect(() => {
    if (content) {
      setInitialHtml(generateHTML(content, extensions));
    }

    return () => {
      setInitialHtml(null);
    };
  }, [content]);

  if (!content || !initialHtml) {
    return (
      <GhostLoader aria-busy="true" style={{ width: '100%', height: 300 }} />
    );
  }

  return (
    <div className={classes.contentEditor}>
      <RichTextEditor
        key={'editor'}
        output="json"
        content={initialHtml}
        onChangeContent={(json) => {
          if (skipFirstChange.current) {
            skipFirstChange.current = false;
            return;
          }
          if (
            !ArticleUtils.jsonEqual(json, content) &&
            !ArticleUtils.isJsonContentEmpty(json)
          ) {
            onChange(json);
          }
        }}
        extensions={extensions as any}
      />
    </div>
  );
};
